/* ══════════════════════════════════════════════════════════════════
   🎁 GLOBAL FREE GIFT AUTO ADD/REMOVE — v15
   ══════════════════════════════════════════════════════════════════
   Loaded ONCE, globally, from layout/theme.liquid. Requires
   sleep-setup-widget.js to be loaded BEFORE this file (its "LIVE CART
   ITEM PRICE SYNC" section is what populates window.__swItemGiftData
   and exposes window.__swSyncAllCartItemPrices — both used below).

   ══════════════════════════════════════════════════════════════════
   v8 CHANGE — productlist/mattress calls were removed entirely from
   this file, in favor of reading gift data for free off
   window.__swItemGiftData (populated by sleep-setup-widget.js's
   price-sync from the SAME /apis/shopify/price/update call it makes
   for pricing — confirmed response shape: data.fullData.forgiftlist).

   v15 CHANGE — that price/update-sourced gift data has turned out to
   be unreliable in practice (empty forgiftlist for a product that
   genuinely has a configured gift, confirmed against the
   productlist/mattress category API's own `gifts` array for the same
   product id). productlist/mattress is reintroduced here, but ONLY as
   a FALLBACK: price/update is still tried first (usually enough, zero
   extra calls), and productlist/mattress is only fetched — once,
   cached by product id — when a mattress line's price/update response
   came back with no gift data. The bridge from a Shopify cart line
   back to "our API's product id" is the `_product_id` LINE-ITEM
   PROPERTY, which every add-to-cart flow already stamps for exactly
   this purpose (Shopify itself has no concept of our API's product
   ids — properties are the only place that mapping can live).

   IMPORTANT — gift line items (`_is_free_gift: 'true'`) never go
   through price/update at all (that filter lives in sleep-setup-
   widget.js). Their Shopify variant price (₹1) is the only price
   they should ever show — this file never touches their price,
   only their quantity/existence.
   ══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  if (window.__swGlobalFreeGiftInit) return;
  window.__swGlobalFreeGiftInit = true;

  console.log('🎁 Global Free Gift auto add/remove — v15 active (price/update primary, productlist/mattress fallback)');

  /* ── CONFIG ── */
  var GIFT_COLLECTION_HANDLE = 'free-gift';
  var CART_ROW_SELECTOR = '.custom-cart-card';

  var giftCollectionCache = null;
  var giftCollectionPromise = null;

  var swGiftKeysCache = {};
  var swItemKeyToVariantId = {};
  var swGiftKeysByParentVariant = {};

  function normTitle(s) {
    return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function silentCartFetch(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-SW-Silent-Cart-Sync': '1'
      },
      body: JSON.stringify(body)
    });
  }

  function getCartItemsSectionId() {
    var el = document.querySelector('cart-items-component');
    return el && el.dataset && el.dataset.sectionId;
  }

  /* A dedicated, shared "cart-drawer-refresh.js" utility owns the
     single, canonical window.swRefreshCartDrawer() implementation —
     including request coalescing, scroll-position preservation across
     the morph, and the window.__swDrawerMorphInFlight coordination
     flag that sleep-setup-widget.js's price sync checks. This file
     delegates to it; the inline fallback below only runs if that
     shared script somehow failed to load. */
  function refreshCartDrawer(freshCartFallback) {
    if (window.swRefreshCartDrawer) {
      window.swRefreshCartDrawer({ delay: 0 });
      return;
    }

    console.warn('🎁 window.swRefreshCartDrawer not found — falling back to a local, less-coordinated refresh. Check that cart-drawer-refresh.js is loaded before free-gift.js.');
    var sectionId = getCartItemsSectionId();
    var morphSectionFn = window.__swMorphSection;

    if (sectionId && morphSectionFn) {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-SW-Silent-Cart-Sync': '1'
        },
        body: JSON.stringify({ sections: sectionId, sections_url: window.location.pathname })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var html = data && data.sections && data.sections[sectionId];
          if (html) {
            try {
              morphSectionFn(sectionId, html, { mode: 'hydration' });
              return;
            } catch (e) {
              console.error('🎁 morphSection() call failed, falling back:', e);
            }
          }
          fallbackRefresh();
        })
        .catch(fallbackRefresh);
    } else {
      fallbackRefresh();
    }

    function fallbackRefresh() {
      var CartAddEventCtor = window.__sleepSetupCartAddEvent || window.__swSleepEssentialsCartAddEvent;
      if (CartAddEventCtor && freshCartFallback) {
        try {
          document.dispatchEvent(new CartAddEventCtor({ resource: freshCartFallback }, 'free-gift', {}));
          return;
        } catch (e) {}
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    }
  }

  /* Gift-collection lookup (to resolve a gift PRODUCT NAME into an
     actual Shopify variant id) is unrelated to productlist/mattress —
     it stays exactly as before: one cheap, cached call. */
  function loadGiftCollection() {
    if (giftCollectionCache) return Promise.resolve(giftCollectionCache);
    if (giftCollectionPromise) return giftCollectionPromise;
    giftCollectionPromise = fetch('/collections/' + GIFT_COLLECTION_HANDLE + '/products.json?limit=250')
      .then(function (r) {
        if (!r.ok) throw new Error('gift collection fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var list = (data.products || []).map(function (p) {
          var variant = (p.variants || []).find(function (v) { return v.available; }) || p.variants[0];
          return { titleNorm: normTitle(p.title), title: p.title, variantId: variant ? variant.id : null };
        }).filter(function (p) { return p.variantId; });
        giftCollectionCache = list;
        console.log('🎁 Gift collection cached:', list.length, 'product(s)');
        return list;
      })
      .catch(function (e) {
        console.error('🎁 Could not load gift collection ("' + GIFT_COLLECTION_HANDLE + '"):', e);
        giftCollectionCache = [];
        return giftCollectionCache;
      });
    return giftCollectionPromise;
  }

  function findGiftVariant(giftName) {
    var needle = normTitle(giftName);
    return (giftCollectionCache || []).find(function (p) { return p.titleNorm === needle; })
        || (giftCollectionCache || []).find(function (p) {
             return p.titleNorm.indexOf(needle) !== -1 || needle.indexOf(p.titleNorm) !== -1;
           })
        || null;
  }

  /* ══════════════════════════════════════════════════════════════
     v15 — FALLBACK gift lookup via productlist/mattress.

     Only used when window.__swItemGiftData[item.key] came back empty
     from price/update for a mattress item. Bridges the Shopify cart
     line back to "our API's product id" via the `_product_id`
     LINE-ITEM PROPERTY (Shopify has no native concept of this id —
     the property is the only place that mapping exists, and every
     add-to-cart flow already stamps it there for exactly this
     purpose).

     Cached PER PRODUCT ID (giftsByApiProductId), and the underlying
     category fetch itself is only ever made ONCE per page load
     (fullMattressListPromise) no matter how many different mattress
     product ids need a fallback lookup — so worst case this costs one
     paginated fetch total, not one per product.
     ══════════════════════════════════════════════════════════════ */
  function getApiId() {
    try { return localStorage.getItem('city_id') || '259'; } catch (e) { return '259'; }
  }

  /* 🆕 FAST PATH — gift list ka STABLE cache (variant + product + city).
     Quantity badalne pe line ka gift list nahi badalta, sirf qty badalti
     hai. Pehle har +/- pe price/update API ke poora khatam hone ka wait
     hota tha (aur price likhne se line key badal jaati thi, jisse
     key-wala cache miss ho jaata tha) — isi wajah se gift 10–15s me
     update hota tha. Ab is cache se gift turant sahi ho jaata hai. */
  var giftsByStableKey = {};
  function giftStableKey(item) {
    var pr = item.properties || {};
    return String(item.variant_id) + '|' + String(pr['_product_id'] || '') + '|' +
      String(pr['_city_id'] || getApiId()) + '|' + String(pr['_state_id'] || '');
  }
  function cachedGiftsFor(item) {
    var byKey = window.__swItemGiftData && window.__swItemGiftData[item.key];
    if (byKey && byKey.length) return byKey;
    return giftsByStableKey[giftStableKey(item)] || null;
  }
  function canUseFastGiftPath(cart) {
    var mattress = (cart.items || []).filter(function (it) {
      return it.properties && it.properties['_is_free_gift'] !== 'true' && it.properties['_sw_category'] === 'mattress';
    });
    return mattress.every(function (it) { return cachedGiftsFor(it) !== null; });
  }

  var PRODUCTLIST_API_BASE = 'https://sf-backend.mysleepwell.com/s1/shopify/productlist/';
  var MAX_FALLBACK_PAGES = 20;
  var giftsByApiProductId = {};
  var fullMattressListPromise = null;

  function fetchMattressListPage(pageno) {
    return fetch(PRODUCTLIST_API_BASE + 'mattress', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        city_id: Number(getApiId()) || 259,
        pageno: pageno,
        size: [], feel: [], type: [], look: [], thickness: [], series: [], benefit: [], warranty: [],
        order: 'relevance_seq', price_min: 0, price_max: 200000
      })
    }).then(function (r) { return r.json(); });
  }

  function fetchFullMattressListOnce() {
    if (fullMattressListPromise) return fullMattressListPromise;

    fullMattressListPromise = new Promise(function (resolve) {
      var all = [];
      var seen = {};
      function loadPage(pageno) {
        fetchMattressListPage(pageno)
          .then(function (data) {
            var products = (data && data.products) || [];
            if (!products.length) { finish(true); return; }
            products.forEach(function (p) {
              var key = p.id != null ? p.id : JSON.stringify(p);
              if (!seen[key]) { seen[key] = true; all.push(p); }
            });
            var totalPages = data && (data.total_pages || data.totalpages ||
              (data.pagination && data.pagination.totalpages));
            if (totalPages && pageno >= totalPages) { finish(true); return; }
            if (pageno >= MAX_FALLBACK_PAGES) {
              // v16 — hit the safety cap WITHOUT a totalPages field ever
              // confirming we'd reached the real last page. There could
              // be more mattress products beyond this that we never
              // saw — if a fallback lookup for a specific product id
              // still comes back empty after this, that product might
              // simply be further out than page 20, not necessarily
              // giftless. Flag it loudly so this isn't mistaken for
              // "no gift configured."
              console.warn('🎁 Fallback: productlist/mattress pagination hit the ' + MAX_FALLBACK_PAGES + '-page safety cap without the API ever confirming a totalPages/total_pages value — there may be MORE mattress products beyond page ' + MAX_FALLBACK_PAGES + ' that were never fetched. If a specific product\'s gift lookup still comes up empty after this, that could be why — check whether the API response includes total_pages, and raise MAX_FALLBACK_PAGES if genuinely needed.');
              finish(false);
              return;
            }
            loadPage(pageno + 1);
          })
          .catch(function () { finish(false); });
      }
      function finish(completeCoverage) {
        all.forEach(function (p) {
          if (p && p.id != null) giftsByApiProductId[String(p.id)] = p.gifts || [];
        });
        console.log('🎁 Fallback: productlist/mattress fetched once (' + all.length + ' product(s)' + (completeCoverage ? '' : ', coverage may be INCOMPLETE — see warning above') + ') to backfill gift data price/update didn\'t return.');
        resolve();
      }
      loadPage(1);
    });

    return fullMattressListPromise;
  }

  // Returns a Promise resolving to the gift-definition array for the
  // given API product id, using the fallback list (fetching it once,
  // lazily, only the first time this is actually needed).
  function getGiftsViaFallback(apiProductId) {
    if (!apiProductId) return Promise.resolve([]);
    if (giftsByApiProductId[String(apiProductId)] !== undefined) {
      return Promise.resolve(giftsByApiProductId[String(apiProductId)]);
    }
    return fetchFullMattressListOnce().then(function () {
      return giftsByApiProductId[String(apiProductId)] || [];
    });
  }

  function detectSizeCategory(item) {
    // v17 — BUGFIX: custom-size mattresses reuse a fallback Shopify
    // variant (Shopify has no real variant for an arbitrary custom
    // size) — that variant's own `variant_title`/`options_with_values`
    // describe the PLACEHOLDER size, not what the customer actually
    // chose. Confirmed bug: a custom Queen/King order was always read
    // as "single", because the fallback variant literally IS a Single
    // variant (e.g. "Single / 5 / 72X30") — its title contains the
    // word "single", never "queen"/"king", so this always fell through
    // to the 'single' default regardless of the real chosen size.
    //
    // The "Custom Size" line property (e.g. "Queen | 72.6\" × 71.9\"
    // (5\" thick)") is the only place holding the REAL chosen category
    // for such items — stamped by the PDP using the server-calculated
    // size category, not guessed from width. Check it first; only fall
    // back to variant_title/options for genuinely standard-size items
    // that don't have this property at all.
    var customSizeLabel = item.properties && item.properties['Custom Size'];
    if (customSizeLabel) {
      var customNorm = normTitle(customSizeLabel);
      if (customNorm.indexOf('king') !== -1) return 'king';
      if (customNorm.indexOf('queen') !== -1) return 'queen';
      return 'single';
    }

    var haystack = normTitle(item.variant_title) + ' ' +
      (item.options_with_values || []).map(function (o) { return o.value; }).join(' ').toLowerCase();
    if (haystack.indexOf('king') !== -1) return 'king';
    if (haystack.indexOf('queen') !== -1) return 'queen';
    return 'single';
  }

  function perUnitQtyForSize(giftDef, sizeCategory) {
    if (sizeCategory === 'king' && giftDef.qty_king != null) return giftDef.qty_king;
    if (sizeCategory === 'queen' && giftDef.qty_queen != null) return giftDef.qty_queen;
    if (giftDef.qty_single != null) return giftDef.qty_single;
    return 1;
  }

  function lockGiftRow(row) {
    if (!row) return;
    row.dataset.swGiftQtyLocked = 'true';

    row.querySelectorAll('input[type="number"], input[name*="quantity" i]').forEach(function (input) {
      input.setAttribute('disabled', 'disabled');
      input.style.pointerEvents = 'none';
      input.style.opacity = '0.6';
    });
    row.querySelectorAll('button').forEach(function (btn) {
      var t = (btn.textContent || '').trim();
      var isStepperBtn = btn.matches('[name="plus"],[name="minus"],[data-quantity-selector-button]') || /^[+\-−]$/.test(t);
      if (isStepperBtn) {
        btn.setAttribute('disabled', 'disabled');
        btn.style.opacity = '0.35';
        btn.style.pointerEvents = 'none';
        btn.style.cursor = 'not-allowed';
      }
    });
    // 🆕 Hide the remove/delete button on gift rows — a free gift is
    // tied entirely to its parent mattress and is added/removed
    // automatically by this script's reconciliation; the shopper
    // should never be able to delete it manually (it would just get
    // re-added on the next sync pass anyway).
    var removeBtn = row.querySelector('.custom-cart-card__remove');
    if (removeBtn) {
      removeBtn.style.setProperty('display', 'none', 'important');
    }
  }

  function disableGiftQuantityControls() {
    fetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (cart) {
      swGiftKeysCache = {};
      swItemKeyToVariantId = {};
      swGiftKeysByParentVariant = {};

      (cart.items || []).forEach(function (it) {
        swItemKeyToVariantId[it.key] = String(it.variant_id);
        if (it.properties && it.properties['_is_free_gift'] === 'true') {
          swGiftKeysCache[it.key] = true;
          var parentId = it.properties['_gift_for_variant_id'];
          if (parentId) {
            if (!swGiftKeysByParentVariant[parentId]) swGiftKeysByParentVariant[parentId] = [];
            if (swGiftKeysByParentVariant[parentId].indexOf(it.key) === -1) {
              swGiftKeysByParentVariant[parentId].push(it.key);
            }
          }
        }
      });

      Object.keys(swGiftKeysCache).forEach(function (key) {
        var row;
        try { row = document.querySelector(CART_ROW_SELECTOR + '[data-key="' + CSS.escape(key) + '"]'); }
        catch (e) { row = null; }
        if (row) lockGiftRow(row);
      });
    }).catch(function () {});
  }
  window.swDisableGiftQuantityControls = disableGiftQuantityControls;

  function disableGiftQuantityControlsWithRetries() {
    [0, 250, 600, 1200, 2000].forEach(function (delay) {
      setTimeout(disableGiftQuantityControls, delay);
    });
  }

  var giftLockObserver = new MutationObserver(function (mutations) {
    var keys = Object.keys(swGiftKeysCache);
    if (!keys.length) return;

    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        var rows = [];
        if (node.matches && node.matches(CART_ROW_SELECTOR)) rows = [node];
        else if (node.querySelectorAll) rows = Array.prototype.slice.call(node.querySelectorAll(CART_ROW_SELECTOR));

        rows.forEach(function (row) {
          var key = row.getAttribute('data-key');
          if (key && swGiftKeysCache[key] && row.dataset.swGiftQtyLocked !== 'true') {
            lockGiftRow(row);
            console.log('🔒 Instantly re-locked gift row after re-render:', key);
          }
        });
      });
    });
  });
  giftLockObserver.observe(document.body, { childList: true, subtree: true });

var syncInFlight = false;
  var syncQueued = false;
  // 🆕 Tracks the setTimeout handle for a queued follow-up runGiftSync()
  // call (see finish() below) — lets __swRunGiftSync()'s polling loop
  // correctly wait through the brief gap between syncQueued being
  // cleared and the follow-up run actually starting.
  var pendingFollowupSyncTimer = null;

  var fastRemoveInFlight = false;
  var fastRemoveQueued = false;
  var fastRemoveDebounce = null;

  function removeOrphanedGiftsFast() {
    if (fastRemoveInFlight) { fastRemoveQueued = true; return Promise.resolve(); }
    fastRemoveInFlight = true;

    return fetch('/cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var realVariantIds = {};
        items.forEach(function (it) {
          if (!(it.properties && it.properties['_is_free_gift'] === 'true')) {
            realVariantIds[String(it.variant_id)] = true;
          }
        });

        var giftItems = items.filter(function (it) {
          return it.properties && it.properties['_is_free_gift'] === 'true';
        });

        var orphaned;
        if (!Object.keys(realVariantIds).length) {
          // Cart has no real items left at all → every gift is orphaned.
          orphaned = giftItems;
        } else {
          orphaned = giftItems.filter(function (it) {
            var parentIdsRaw = (it.properties['_gift_for_variant_ids'] || it.properties['_gift_for_variant_id'] || '');
            var parentIds = String(parentIdsRaw).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
            // No parent info recorded on the line → don't guess here,
            // leave it for the full reconciliation to judge.
            if (!parentIds.length) return false;
            return !parentIds.some(function (pid) { return realVariantIds[pid]; });
          });
        }

        if (!orphaned.length) return false;

        return Promise.all(orphaned.map(function (it) {
          console.log('🎁 Fast-remove: orphaned gift (parent item already removed):', it.product_title);
          return silentCartFetch('/cart/change.js', { id: it.key, quantity: 0 }).catch(function (e) {
            console.error('🎁 Fast-remove failed for', it.product_title, ':', e);
          });
        })).then(function () { return true; });
      })
      .catch(function (e) {
        console.error('🎁 Fast-remove pass failed:', e);
        return false;
      })
      .then(function (removed) {
        fastRemoveInFlight = false;
        if (fastRemoveQueued) {
          fastRemoveQueued = false;
          setTimeout(removeOrphanedGiftsFast, 50);
        }
        if (removed) {
          return fetch('/cart.js', { cache: 'no-store' })
            .then(function (r) { return r.json(); })
            .then(function (freshCart) { refreshCartDrawer(freshCart); })
            .catch(function () {});
        }
      });
  }

  function scheduleFastRemove() {
    clearTimeout(fastRemoveDebounce);
    fastRemoveDebounce = setTimeout(removeOrphanedGiftsFast, 60); // 🆕 150 → 60ms
  }

  /* ══════════════════════════════════════════════════════════════
     FULL RECONCILIATION SYNC.
     ...
     ══════════════════════════════════════════════════════════════ */
  function runGiftSync() {

    if (syncInFlight) { syncQueued = true; return; }
    syncInFlight = true;

    var anyMutation = false;

    var pricePromise = window.__swSyncAllCartItemPrices
      ? window.__swSyncAllCartItemPrices()
      : Promise.resolve();
    // 🆕 checkout settle ise await karta hai (price bhi final hona chahiye)
    swLastPricePromise = Promise.resolve(pricePromise).catch(function () {});

    // 🆕 FAST PATH: saare mattress items ka gift list cache me hai →
    // price sync ka wait mat karo, gift turant reconcile karo. Price
    // sync background me chalta rehta hai (checkout uska wait karega).
    var promise = fetch('/cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart0) {
        if (canUseFastGiftPath(cart0)) {
          console.log('🎁⚡ Gift sync fast path — cached gift data, not waiting for price sync');
          return cart0;
        }
        return pricePromise.then(function () {
          return fetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); });
        });
      })
      .then(function (cart) {
        var realItems = (cart.items || []).filter(function (it) {
          return !(it.properties && it.properties['_is_free_gift'] === 'true');
        });
        var giftItems = (cart.items || []).filter(function (it) {
          return it.properties && it.properties['_is_free_gift'] === 'true';
        });

        if (!realItems.length) {
          if (!giftItems.length) return Promise.resolve();
          anyMutation = true;
          return Promise.all(giftItems.map(function (it) {
            console.log('🎁 Removing free gift (cart has no real items left):', it.product_title);
            return silentCartFetch('/cart/change.js', { id: it.key, quantity: 0 });
          }));
        }

        return loadGiftCollection().then(function () {
          var expectedMap = {}; // giftVariantId -> { giftVariantId, giftName, expectedQty, contributingParentVariantIds: Set-like object }

          var mattressItems = realItems.filter(function (item) {
            // Only mattress lines are ever gift-eligible. `_sw_category`
            // is the definitive gate — every add-to-cart flow (widget,
            // Sleep Essentials section, PDP) stamps it.
            return item.properties && item.properties['_sw_category'] === 'mattress';
          });

          // v15 — resolve each mattress item's gifts async: try the
          // price/update-sourced cache first (free, no network call);
          // only fall back to productlist/mattress (via `_product_id`)
          // if that cache came back empty for this item.
          return Promise.all(mattressItems.map(function (item) {
            var cached = cachedGiftsFor(item) || [];
            var giftsPromise = cached.length
              ? Promise.resolve(cached)
              : getGiftsViaFallback(item.properties['_product_id']).then(function (fallbackGifts) {
                  if (fallbackGifts.length) {
                    console.log('🎁 Fallback: found', fallbackGifts.length, 'gift(s) via productlist/mattress for _product_id', item.properties['_product_id'], '(price/update had none cached)');
                  } else {
                    console.warn('🎁 No gift data for mattress item', item.key, '(', item.product_title, ', _product_id', item.properties['_product_id'], ') from EITHER price/update or the productlist/mattress fallback — this product genuinely has no gift configured for the current city/state.');
                  }
                  return fallbackGifts;
                });

            return giftsPromise.then(function (gifts) {
              giftsByStableKey[giftStableKey(item)] = gifts || []; // 🆕 fast path ke liye yaad rakho
              if (!gifts.length) return;

              // v12 — location context to copy onto the gift line, same
              // as the parent mattress line carries it. Only include
              // keys that actually have a value on the parent.
              var parentLocationProps = {};
              ['_city_id', '_city_name', '_state_id', '_state_name', 'hl_pin'].forEach(function (k) {
                var v = item.properties && item.properties[k];
                if (v !== undefined && v !== null && v !== '') parentLocationProps[k] = v;
              });

              var sizeCategory = detectSizeCategory(item);
              gifts.forEach(function (giftDef) {
                var match = findGiftVariant(giftDef.gift_prod_name);
                if (!match) {
                  console.warn('🎁 No gift-collection product matches "' + giftDef.gift_prod_name + '"');
                  return;
                }
                var perUnitQty = perUnitQtyForSize(giftDef, sizeCategory);
                var parentVariantId = String(item.variant_id);
                var giftVariantId = String(match.variantId);
                // _base_product_id — the API product id of the mattress
                // this gift is attached to (its `_product_id` line
                // property). For a gift shared by multiple mattresses
                // (v16 merge) we keep every contributing base id,
                // comma-joined, so nothing is lost.
                var baseApiProductId = item.properties && item.properties['_product_id'];
                // v16 — BUGFIX: key by giftVariantId ONLY, not by
                // (parentVariantId + giftVariantId). Previously, two
                // DIFFERENT mattresses that both qualify for the exact
                // same gift product each got their OWN separate cart
                // line for it (e.g. "Cloud pillow ×2" and a second,
                // separate "Cloud pillow ×3" line) — visually two
                // duplicate gift cards instead of one combined
                // "Cloud pillow ×5". Now every mattress contributing to
                // the SAME gift product accumulates into ONE shared
                // entry/line, regardless of which mattress(es) it came
                // from.
                var addQty = perUnitQty * (parseInt(item.quantity, 10) || 1);

                if (expectedMap[giftVariantId]) {
                  expectedMap[giftVariantId].expectedQty += addQty;
                  expectedMap[giftVariantId].contributingParentVariantIds[parentVariantId] = true;
                  if (baseApiProductId != null && baseApiProductId !== '') {
                    expectedMap[giftVariantId].baseApiProductIds[String(baseApiProductId)] = true;
                  }
                } else {
                  var parentSet = {};
                  parentSet[parentVariantId] = true;
                  var baseSet = {};
                  if (baseApiProductId != null && baseApiProductId !== '') {
                    baseSet[String(baseApiProductId)] = true;
                  }
                  expectedMap[giftVariantId] = {
                    giftVariantId: giftVariantId,
                    giftName: match.title,
                    expectedQty: addQty,
                    contributingParentVariantIds: parentSet,
                    baseApiProductIds: baseSet,
                    // v12 — same idea as a real product line's
                    // `_product_id`/`_length`/`_breadth`/`_thickness`:
                    // giftDef comes from either price/update's
                    // forgiftlist or the productlist/mattress fallback
                    // — both shapes carry the gift product's own id
                    // and dimensions (gift_prod_id, length, breadth,
                    // thickness).
                    giftApiProductId: giftDef.gift_prod_id,
                    giftLength: giftDef.length,
                    giftBreadth: giftDef.breadth,
                    giftThickness: giftDef.thickness,
                    parentLocationProps: parentLocationProps
                  };
                }
              });
            });
          })).then(function () {
function buildGiftProperties(exp) {
            var parentIds = Object.keys(exp.contributingParentVariantIds || {});
            var baseIds = Object.keys(exp.baseApiProductIds || {});
            var props = {
              '_is_free_gift': 'true',
              '_gift_for_variant_ids': parentIds.join(','),
              '_sw_category': 'gift'
            };
            if (baseIds.length)              props['_base_product_id'] = baseIds.join(',');
            if (exp.giftApiProductId != null) props['_product_id'] = String(exp.giftApiProductId);
            if (exp.giftLength != null)      props['_length']    = String(exp.giftLength);
            if (exp.giftBreadth != null)     props['_breadth']   = String(exp.giftBreadth);
            if (exp.giftThickness != null)   props['_thickness'] = String(exp.giftThickness);
            var merged = Object.assign(props, exp.parentLocationProps || {});
            
            delete merged['_api_price'];
            delete merged['_api_compare'];
            delete merged['_api_discount'];
            return merged;
          }

          var expected = Object.keys(expectedMap).map(function (k) { return expectedMap[k]; });

          var workingItems = cart.items.slice();
          var matchedGiftKeys = {};
          var chain = Promise.resolve();

          expected.forEach(function (exp) {
            chain = chain.then(function () {
              // v16 — match purely by giftVariantId now (no more
              // per-parent matching) — there should only ever be ONE
              // cart line per distinct gift product going forward.
              var existingLine = workingItems.find(function (it) {
                return it.properties && it.properties['_is_free_gift'] === 'true' &&
                       String(it.variant_id) === exp.giftVariantId;
              });

              if (existingLine) {
                matchedGiftKeys[existingLine.key] = true;
                var needsQtyFix = existingLine.quantity !== exp.expectedQty;
                var needsPropsFix = !(existingLine.properties && existingLine.properties['_product_id']) ||
                  !(existingLine.properties && existingLine.properties['_gift_for_variant_ids']) ||
                  !(existingLine.properties && existingLine.properties['_base_product_id']);

                var needsLocationFix = Object.keys(exp.parentLocationProps || {}).some(function (k) {
                  return (existingLine.properties && existingLine.properties[k]) !== exp.parentLocationProps[k];
                });

              if (needsQtyFix || needsPropsFix || needsLocationFix) {
                  anyMutation = true;
                  var mergedGiftProps = Object.assign({}, existingLine.properties || {}, buildGiftProperties(exp));
                  delete mergedGiftProps['_gift_for_variant_id']; // drop the old singular property if present
                  delete mergedGiftProps['_api_price'];
                  delete mergedGiftProps['_api_compare'];
                  delete mergedGiftProps['_api_discount'];
                  var reason = needsQtyFix ? 'Correcting gift quantity' : (needsLocationFix ? 'Syncing gift location to parent (pincode changed)' : 'Backfilling gift properties');
                  console.log('🎁 ' + reason + ' for', exp.giftName, needsQtyFix ? (':' + existingLine.quantity + ' → ' + exp.expectedQty) : '');
                  return silentCartFetch('/cart/change.js', { id: existingLine.key, quantity: exp.expectedQty, properties: mergedGiftProps })
                    .then(function (res) {
                      if (!res.ok) throw new Error('gift correction failed: ' + res.status);
                      existingLine.quantity = exp.expectedQty;
                      existingLine.properties = mergedGiftProps;
                    })
                    .catch(function (e) {
                      console.error('🎁 Failed to correct/backfill gift line for', exp.giftName, ':', e);
                    });
                }
                return null;
              }

              anyMutation = true;
              return silentCartFetch('/cart/add.js', {
                id: exp.giftVariantId,
                quantity: exp.expectedQty,
                properties: buildGiftProperties(exp)
              }).then(function (res) {
                if (!res.ok) throw new Error('gift add failed: ' + res.status);
                console.log('🎁 Free gift added:', exp.giftName, '×' + exp.expectedQty, 'for variants', Object.keys(exp.contributingParentVariantIds).join(','));
                var placeholderKey = '__pending__' + exp.giftVariantId;
                matchedGiftKeys[placeholderKey] = true;
                workingItems.push({
                  key: placeholderKey,
                  variant_id: exp.giftVariantId,
                  quantity: exp.expectedQty,
                  properties: buildGiftProperties(exp)
                });
              }).catch(function (e) {
                console.error('🎁 Failed to add free gift:', e);
              });
            });
          });

          chain = chain.then(function () {
            var toRemove = giftItems.filter(function (it) { return !matchedGiftKeys[it.key]; });
            if (!toRemove.length) return null;
            anyMutation = true;
            return Promise.all(toRemove.map(function (it) {
              console.log('🎁 Removing free gift no longer needed:', it.product_title, '(was for variant(s)', (it.properties && (it.properties['_gift_for_variant_ids'] || it.properties['_gift_for_variant_id'])) + ')');
              return silentCartFetch('/cart/change.js', { id: it.key, quantity: 0 });
            }));
          });

          return chain;
          });
        });
      })
      .catch(function (e) {
        console.error('🎁 Gift sync failed:', e);
      })
      .then(function () {
        return finish();
      });

    function finish() {
      syncInFlight = false;

      var afterFinish = anyMutation
        ? fetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (freshCart) {
            refreshCartDrawer(freshCart);
          }).catch(function () {})
        : Promise.resolve();

           return afterFinish.then(function () {
        if (!syncQueued) swLastGiftSyncDoneAt = Date.now(); // 🆕 settle fast-exit ke liye
        if (syncQueued) {
          syncQueued = false;
          pendingFollowupSyncTimer = setTimeout(function () {
            pendingFollowupSyncTimer = null;
            runGiftSync();
          }, 150);
        }
        if (anyMutation) {
          disableGiftQuantityControlsWithRetries();
        } else {
          setTimeout(disableGiftQuantityControls, 250);
        }
      });
    }

    return promise;
  }
     window.__swRunGiftSync = function () {
    return new Promise(function (resolve) {
      clearTimeout(fastRemoveDebounce);
      // 🆕 FIX: cancel any pending event-driven scheduleSync() debounce
      // (e.g. from the cart/add fetch interceptor's 'sleep-setup:cart-
      // changed' dispatch) so it can't fire a SECOND, independent
      // runGiftSync() pass that this function's polling loop below
      // doesn't know to wait for. Only the explicit runGiftSync() call
      // triggered right here should govern when this promise resolves.
      clearTimeout(debounceTimer);
      syncScheduled = false; // 🆕 pending debounce cancel ho gaya
      removeOrphanedGiftsFast().then(function () {
        var check = setInterval(function () {
          // 🆕 FIX: also wait out `syncQueued` AND any already-scheduled
          // follow-up run — previously this only checked syncInFlight,
          // which could flip back to false for a brief window between
          // one sync finishing and its queued follow-up actually
          // starting (a setTimeout in between), causing this promise to
          // resolve too early and the caller (e.g. swBuyNowHandler) to
          // redirect before the real gift-add API call had actually
          // completed.
          if (!syncInFlight && !syncQueued && !pendingFollowupSyncTimer) {
            clearInterval(check);
            resolve();
          }
        }, 100);
        runGiftSync();
        setTimeout(function () { clearInterval(check); resolve(); }, 8000);
      });
    });
  };

  var debounceTimer = null;
  var syncScheduled = false; // 🆕 debounce pending hai (checkout settle ke liye)
  function scheduleSync(delay) {
    clearTimeout(debounceTimer);
    syncScheduled = true;
    debounceTimer = setTimeout(function () {
      syncScheduled = false;
      runGiftSync();
    }, delay == null ? 400 : delay);
  }

  ['cart:update', 'cart:updated', 'cart:refresh', 'cart:change', 'sleep-setup:cart-changed'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      scheduleFastRemove(); // orphaned gift turant hataao (60ms debounce)
      scheduleSync(120);    // 🆕 400 → 120ms — full reconciliation (add/qty-fix)
    });
  });

  /* ══════════════════════════════════════════════════════════════
     🆕 CART SETTLE — checkout se pehle cart poori tarah "stable" ho.

     Bug: qty minus karke turant checkout dabane pe GoKwik khul jaata
     tha jabki gift abhi remove/update nahi hua tha (aur kabhi native
     qty request bhi 300ms debounce me pending hoti thi). Ab checkout
     button window.__swWaitCartSettled() ka wait karta hai:
       1. koi bhi /cart/add|change|update|clear request chal rahi ho,
          ya qty selector ka change abhi bheja na gaya ho → wait
       2. gift sync (orphan remove + qty fix/add) poora hone do
       3. price sync (price/update + cart write) poora hone do
       4. 150ms tak cart me koi nayi activity na ho
     ══════════════════════════════════════════════════════════════ */
  var swLastPricePromise = Promise.resolve();
  var swCartReqInFlight = 0;
  var swLastCartActivity = 0;
  var swQtyPending = false;
  var swQtyPendingTimer = null;
  var swQuietUntil = 0; // non-silent cart change ke baad sleep-setup-widget 300ms baad event bhejta hai
  var swLastRealChange = 0;      // shopper ka asli cart change (non-silent)
  var swLastGiftSyncDoneAt = 0;  // aakhri poora gift sync kab khatam hua
  function swMarkCartActivity() { swLastCartActivity = Date.now(); }
  function swMarkRealChange() { swLastRealChange = Date.now(); swMarkCartActivity(); }

  var SW_CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var swFetchBeforeSettle = window.fetch;
  window.fetch = function (input, init) {
    var url = '';
    try { url = typeof input === 'string' ? input : (input && input.url) || String(input || ''); } catch (e) {}
    if (!SW_CART_MUTATION_RE.test(url)) return swFetchBeforeSettle.apply(this, arguments);
    var isSilent = false;
    try { isSilent = !!(init && init.headers && (init.headers['X-SW-Silent-Cart-Sync'] || (init.headers.get && init.headers.get('X-SW-Silent-Cart-Sync')))); } catch (e) {}
    swCartReqInFlight++;
    swMarkCartActivity();
    function done() {
      swCartReqInFlight = Math.max(0, swCartReqInFlight - 1);
      swMarkCartActivity();
      // sleep-setup-widget.js non-silent change ke 300ms baad
      // 'sleep-setup:cart-changed' bhejta hai — us event tak ruko
      if (!isSilent) {
        swQuietUntil = Math.max(swQuietUntil, Date.now() + 400);
        swMarkRealChange();
      }
    }
    if (!isSilent) swMarkRealChange();
    var p;
    try { p = swFetchBeforeSettle.apply(this, arguments); } catch (err) { done(); throw err; }
    return Promise.resolve(p).then(
      function (res) { done(); return res; },
      function (err) { done(); throw err; }
    );
  };

  // Native cart qty +/- : 300ms debounce ke baad request jaati hai —
  // us beech bhi cart "busy" maano.
  document.addEventListener('quantity-selector:update', function () {
    swQtyPending = true;
    swMarkRealChange();
    clearTimeout(swQtyPendingTimer);
    swQtyPendingTimer = setTimeout(function () { swQtyPending = false; }, 4000); // safety
  });
  document.addEventListener('sleep-setup:cart-changed', swMarkCartActivity);
  ['cart:update', 'cart:error'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      swQtyPending = false;
      clearTimeout(swQtyPendingTimer);
      swMarkCartActivity();
    });
  });

  function swCartIdle() {
    return swCartReqInFlight === 0 && !swQtyPending && !syncScheduled &&
      !syncInFlight && !syncQueued && !pendingFollowupSyncTimer && !fastRemoveInFlight &&
      !window.__swDrawerMorphInFlight &&
      Date.now() >= swQuietUntil &&
      (Date.now() - swLastCartActivity) >= 150;
  }
  window.__swIsCartBusy = function () { return !swCartIdle(); };

  window.__swWaitCartSettled = function (opts) {
    opts = opts || {};
    var maxMs = opts.maxMs || 12000;
    var start = Date.now();
    function timedOut() { return Date.now() - start > maxMs; }
    function waitIdle() {
      return new Promise(function (resolve) {
        (function tick() {
          if (timedOut()) return resolve(false);
          if (swCartIdle()) return resolve(true);
          setTimeout(tick, 40);
        })();
      });
    }
    return waitIdle()
      .then(function (idle) {
        // 🆕 Cart me aakhri gift sync ke baad koi asli change nahi hua →
        // dobara sync mat chalao, seedha checkout (≈0.2s).
        if (idle && swLastGiftSyncDoneAt && swLastGiftSyncDoneAt > swLastRealChange) {
          return swLastPricePromise.then(function () { return 'fast'; });
        }
        return (window.__swRunGiftSync ? window.__swRunGiftSync() : Promise.resolve())
          .then(function () { return swLastPricePromise; });
      })
      .then(waitIdle)
      .then(function (ok) {
        console.log(ok ? '✅ Cart settled in ' + (Date.now() - start) + 'ms' : '⏱️ Cart settle timed out after ' + maxMs + 'ms');
        return ok;
      })
      .catch(function (e) {
        console.error('Cart settle failed:', e);
        return false;
      });
  };

  // v8 — only pre-warm the gift-collection lookup on load. There is
  // no category catalog to pre-warm anymore; gift definitions arrive
  // lazily, per item, whenever price-sync runs for that item.
  loadGiftCollection().then(function () {
    console.log('🎁 Gift collection cache pre-warmed');
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scheduleSync(800); });
  } else {
    scheduleSync(800);
  }
})();
